import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { CheckCircle, XCircle, Loader2, ChefHat } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { createLogger } from '@/lib/logger';

const log = createLogger('EmailConfirmPage');

type ConfirmationStatus = 'loading' | 'success' | 'error';

export default function EmailConfirmPage() {
  const [status, setStatus] = useState<ConfirmationStatus>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const handleEmailConfirmation = async () => {
      try {
        // Supabase gère automatiquement le token dans l'URL
        const { data, error } = await supabase.auth.getSession();

        if (error) {
          throw error;
        }

        if (data.session) {
          setStatus('success');
        } else {
          // Vérifier si c'est une confirmation d'email via le hash
          const hashParams = new URLSearchParams(window.location.hash.substring(1));
          const accessToken = hashParams.get('access_token');
          const type = hashParams.get('type');

          if (type === 'signup' || type === 'email_change' || accessToken) {
            // Attendre que Supabase traite le token
            setTimeout(async () => {
              const { data: sessionData } = await supabase.auth.getSession();
              if (sessionData.session) {
                setStatus('success');
              } else {
                setStatus('success'); // L'email est confirmé même sans session active
              }
            }, 1000);
          } else {
            setStatus('success');
          }
        }
      } catch (error) {
        log.error('Erreur de confirmation', error);
        setStatus('error');
        setErrorMessage(error instanceof Error ? error.message : 'Une erreur est survenue');
      }
    };

    handleEmailConfirmation();
  }, [searchParams]);

  const handleGoToLogin = () => {
    navigate('/login');
  };

  const handleGoToDashboard = () => {
    navigate('/dashboard');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-500 rounded-2xl shadow-lg mb-4">
            <ChefHat className="w-9 h-9 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">StockChef</h1>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          {status === 'loading' && (
            <>
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center">
                  <Loader2 className="w-10 h-10 text-orange-500 animate-spin" />
                </div>
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Vérification en cours...
              </h2>
              <p className="text-gray-500">
                Nous confirmons votre adresse email
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-10 h-10 text-green-500" />
                </div>
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Email confirmé !
              </h2>
              <p className="text-gray-500 mb-6">
                Votre adresse email a été vérifiée avec succès.
                Vous pouvez maintenant accéder à votre espace StockChef.
              </p>
              <div className="space-y-3">
                <Button
                  onClick={handleGoToDashboard}
                  className="w-full bg-orange-500 hover:bg-orange-600"
                >
                  Accéder à mon espace
                </Button>
                <Button
                  onClick={handleGoToLogin}
                  variant="outline"
                  className="w-full"
                >
                  Se connecter
                </Button>
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center">
                  <XCircle className="w-10 h-10 text-red-500" />
                </div>
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Erreur de confirmation
              </h2>
              <p className="text-gray-500 mb-2">
                Nous n'avons pas pu confirmer votre email.
              </p>
              {errorMessage && (
                <p className="text-sm text-red-500 mb-6">
                  {errorMessage}
                </p>
              )}
              <div className="space-y-3">
                <Button
                  onClick={handleGoToLogin}
                  className="w-full bg-orange-500 hover:bg-orange-600"
                >
                  Retour à la connexion
                </Button>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-gray-400 mt-6">
          Besoin d'aide ? Contactez-nous à support@stockchef.eu
        </p>
      </div>
    </div>
  );
}
